/**
 * @engine/vfx - Shader Generator
 *
 * Generates GPU shaders for extracted VFX systems.
 *
 * Compute (init/update): Slang modules built by the stage generators
 * (function-per-node codegen, see `.refactor/VFX-COMPILER-DESIGN.md`),
 * compiled to WGSL through the injected Slang compile service.
 * Render (vertex/fragment): WGSL templates from ./renderShaders (unchanged).
 */

import type { VFXGraph } from '../VFXGraph';
import type { VFXNodeInstance } from '../types';
import type { VFXExtractedSystem, VFXCompilerOptions } from './types';
import { DEFAULT_COMPILER_OPTIONS } from './types';
import { WGSLBuilder, generateParticleStruct, generateRenderUniformBindings } from './WGSLEmitter';
import { generateInitStage } from './stages/initStage';
import { generateUpdateStage } from './stages/updateStage';
import { nodesAtLine } from './stages/common';
import { getVFXSlangCompiler, formatSlangDiagnostics } from './SlangCompileService';
import { TweakAllocator, type VFXTweakBinding } from './codegen/Tweaks';

// Render-shader codegen (vertex/fragment WGSL) lives in ./renderShaders — imported for use by
// generateShaderBundle and re-exported here so this module's public surface is unchanged.
import { generateVertexShader, generateFragmentShader } from './renderShaders';
import type { RenderOptions } from './renderShaders';
export { generateVertexShader, generateFragmentShader };
export type { RenderOptions };

// ============================================================================
// GENERATED SHADERS
// ============================================================================

/**
 * Generated shader bundle for a system. All four stages are WGSL (compute
 * stages are compiled from generated Slang; their source is kept for
 * debugging/tests).
 */
export interface GeneratedShaderBundle {
  initShader: string;
  updateShader: string;
  vertexShader: string;
  fragmentShader: string;
  /** Generated Slang source of the init stage (pre-compile, for debugging). */
  initSlang: string;
  /** Generated Slang source of the update stage (pre-compile, for debugging). */
  updateSlang: string;
  /** Codegen warnings (missing builtins, unresolved variables, …). */
  warnings: string[];
  /** Editor live-preview tweak slots (tweakableInputs compiles only). */
  tweaks?: readonly VFXTweakBinding[];
  /** Whether fragment shader samples textures */
  usesTexture: boolean;
  /** Texture variable slot index (0-7) if texture is used, -1 otherwise */
  textureSlot: number;
  /** Rendering options extracted from output node */
  renderOptions: RenderOptions;
  /** Whether this system emits light (vfx.output.light) */
  emitsLight: boolean;
  /** Whether this light system also renders a visible quad */
  lightRenderQuad: boolean;
  /** Whether this system receives Forward+ lighting */
  isLit: boolean;
  /** Light configuration (when emitsLight is true) */
  lightConfig?: {
    radius: number;
    intensity: number;
    falloff: number;
    colorMode: number;
    overrideColor: readonly number[];
    intensityFromAlpha: boolean;
  };
}

/** Raised when the generated Slang fails to compile — carries diagnostics. */
export class VFXShaderCompileError extends Error {
  constructor(
    message: string,
    /** Which stage failed. */
    readonly stage: 'init' | 'update',
    /** The generated Slang source (for debugging). */
    readonly source: string,
    /**
     * Graph node instances the failing diagnostics point at (resolved via the
     * stage source map) — empty when the error is in structural stage code.
     */
    readonly nodeIds: readonly string[] = []
  ) {
    super(message);
    this.name = 'VFXShaderCompileError';
  }
}

// ============================================================================
// RENDER OPTION EXTRACTION
// ============================================================================

/**
 * Extract render options from output node.
 */
function extractRenderOptions(outputNode: VFXNodeInstance | undefined): RenderOptions {
  const defaults: RenderOptions = {
    renderType: 'quad',
    isLit: false,
    softParticle: true,
    stretchMode: 0,
    stretchScale: 1.0,
    blendMode: 0,
    orientMode: 0,
    flipbookColumns: 4,
    flipbookRows: 4,
    lineLength: 1.0,
    lineWidth: 0.5,
    velocityAlign: true,
  };

  if (!outputNode) return defaults;

  const values = outputNode.inputValues ?? {};

  // Determine render type from node type
  switch (outputNode.type) {
    case 'vfx.output.quad':
      defaults.renderType = 'quad';
      break;
    case 'vfx.output.lit_quad':
      defaults.renderType = 'quad';
      defaults.isLit = true;
      break;
    case 'vfx.output.sprite':
      defaults.renderType = 'sprite';
      break;
    case 'vfx.output.point':
      defaults.renderType = 'point';
      break;
    case 'vfx.output.line':
      defaults.renderType = 'line';
      break;
    case 'vfx.output.trail':
      defaults.renderType = 'trail';
      break;
    case 'vfx.output.mesh':
      defaults.renderType = 'mesh';
      break;
    case 'vfx.output.light':
      // Light output uses 'point' renderType (visible when renderQuad is on)
      defaults.renderType = 'point';
      break;
  }

  return {
    renderType: defaults.renderType,
    isLit: defaults.isLit,
    softParticle: (values.softParticle as boolean) ?? defaults.softParticle,
    stretchMode: (values.stretchMode as number) ?? defaults.stretchMode,
    stretchScale: (values.stretchScale as number) ?? defaults.stretchScale,
    blendMode: (values.blendMode as number) ?? defaults.blendMode,
    orientMode: (values.orientMode as number) ?? defaults.orientMode,
    flipbookColumns: (values.columns as number) ?? defaults.flipbookColumns,
    flipbookRows: (values.rows as number) ?? defaults.flipbookRows,
    lineLength: (values.length as number) ?? defaults.lineLength,
    lineWidth: (values.width as number) ?? defaults.lineWidth,
    velocityAlign: (values.velocityAlign as boolean) ?? defaults.velocityAlign,
  };
}

// ============================================================================
// SHADER BUNDLE GENERATION
// ============================================================================

/**
 * Generate all shaders for an extracted system.
 * Async: compute stages go through the injected Slang→WGSL compiler.
 * @throws {VFXShaderCompileError} when a generated stage fails to compile.
 */
export async function generateShaderBundle(
  system: VFXExtractedSystem,
  graph: VFXGraph,
  options: VFXCompilerOptions = {}
): Promise<GeneratedShaderBundle> {
  const opts = { ...DEFAULT_COMPILER_OPTIONS, ...options };

  // Check if output node supports texture (quad/sprite types)
  // These nodes ALWAYS use texture - default texture if not connected
  const outputNodes = system.output.nodes;
  const edges = graph.getEdges();

  let usesTexture = false;
  let textureSlot = -1;
  let firstOutputNode: VFXNodeInstance | undefined;

  for (const outputNode of outputNodes) {
    firstOutputNode = outputNode;

    // Only quad/sprite/lit_quad support textures
    if (outputNode.type !== 'vfx.output.quad' &&
        outputNode.type !== 'vfx.output.sprite' &&
        outputNode.type !== 'vfx.output.lit_quad') {
      continue;
    }

    // Quad/sprite/lit_quad ALWAYS use texture (default if not connected)
    usesTexture = true;

    // Find edge connected to texture input to get specific texture slot
    const textureEdge = edges.find(
      e => e.to.nodeId === outputNode.id && e.to.portId === 'texture'
    );

    if (textureEdge) {
      // Find the source node (should be Get Variable (Texture))
      const sourceNode = graph.getNode(textureEdge.from.nodeId);
      if (sourceNode && sourceNode.type === 'vfx.variable.get_texture') {
        // Get the variableId (texture slot)
        textureSlot = (sourceNode.inputValues.variableId as number) ?? 0;
      }
    }
    break; // Use first output node found
  }

  // Extract render options from output node
  const renderOptions = extractRenderOptions(firstOutputNode);

  // Extract light emission config (vfx.output.light)
  const emitsLight = firstOutputNode?.type === 'vfx.output.light';
  let lightRenderQuad = false;
  let lightConfig: GeneratedShaderBundle['lightConfig'];
  if (emitsLight && firstOutputNode) {
    const v = firstOutputNode.inputValues ?? {};
    lightRenderQuad = (v.renderQuad as boolean) ?? false;
    const rawColor = v.overrideColor as number[] | { r: number; g: number; b: number; a: number } | undefined;
    const overrideColor: number[] = Array.isArray(rawColor)
      ? rawColor
      : rawColor && typeof rawColor === 'object' && 'r' in rawColor
        ? [rawColor.r, rawColor.g, rawColor.b, rawColor.a ?? 1]
        : [1, 1, 1, 1];
    lightConfig = {
      radius: (v.radius as number) ?? 150,
      intensity: (v.intensity as number) ?? 1.0,
      falloff: (v.falloff as number) ?? 2.0,
      colorMode: (v.colorMode as number) ?? 0,
      overrideColor,
      intensityFromAlpha: (v.intensityFromAlpha as boolean) ?? true,
    };

    // When renderQuad is enabled, the light system also renders as a visible
    // quad/sprite — so texture support follows the same rules as vfx.output.quad
    if (lightRenderQuad) {
      usesTexture = true;
    }
  }

  // Generate compute stages (Slang) and compile them to WGSL. In tweak mode
  // the allocator is SHARED between the stages so their slot layouts match.
  const tweaks = opts.tweakableInputs ? new TweakAllocator() : null;
  const init = generateInitStage(system, graph, opts, tweaks);
  const update = generateUpdateStage(system, graph, opts, tweaks);
  const compile = getVFXSlangCompiler();

  const [initOut, updateOut] = await Promise.all([
    compile(init.source, { moduleName: `vfx_init_${sanitizeModuleName(system.id)}` }),
    compile(update.source, { moduleName: `vfx_update_${sanitizeModuleName(system.id)}` }),
  ]);

  if (!initOut.ok || !initOut.wgsl) {
    throw new VFXShaderCompileError(
      `Init stage of "${system.name}" failed to compile:\n${formatSlangDiagnostics(initOut)}`,
      'init',
      init.source,
      diagnosticNodeIds(init.sourceMap, initOut)
    );
  }
  if (!updateOut.ok || !updateOut.wgsl) {
    throw new VFXShaderCompileError(
      `Update stage of "${system.name}" failed to compile:\n${formatSlangDiagnostics(updateOut)}`,
      'update',
      update.source,
      diagnosticNodeIds(update.sourceMap, updateOut)
    );
  }

  // Render shaders (WGSL templates)
  const renderHeader = generateRenderHeader(opts);

  return {
    initShader: initOut.wgsl,
    updateShader: updateOut.wgsl,
    vertexShader: renderHeader + generateVertexShader(renderOptions),
    fragmentShader: generateFragmentShader(usesTexture, renderOptions),
    initSlang: init.source,
    updateSlang: update.source,
    warnings: [...init.warnings, ...update.warnings],
    tweaks: tweaks ? tweaks.all() : undefined,
    usesTexture,
    textureSlot,
    renderOptions,
    emitsLight,
    lightRenderQuad,
    lightConfig,
    isLit: renderOptions.isLit,
  };
}

function sanitizeModuleName(id: string): string {
  return id.replace(/[^A-Za-z0-9_]/g, '_');
}

/** Resolve failing diagnostics to the graph nodes their lines belong to. */
function diagnosticNodeIds(
  sourceMap: import('./stages/common').VFXStageSourceMap,
  out: { diagnostics: readonly { severity: string; line: number }[] }
): string[] {
  const ids = new Set<string>();
  for (const d of out.diagnostics) {
    if (d.severity !== 'error' || d.line <= 0) continue;
    for (const id of nodesAtLine(sourceMap, d.line)) ids.add(id);
  }
  return [...ids];
}

/**
 * Generate render shader header (WGSL — render shaders are still WGSL templates).
 */
function generateRenderHeader(options: VFXCompilerOptions): string {
  const builder = new WGSLBuilder();

  builder.comment('Auto-generated VFX render shader');
  builder.comment('Do not edit manually');
  builder.blank();
  builder.raw(generateParticleStruct(options.attributes));
  builder.blank();
  builder.raw(generateRenderUniformBindings());
  builder.blank();

  return builder.build();
}

// ============================================================================
// TEXTURE BINDING EXTRACTION
// ============================================================================

/**
 * Texture binding info extracted from graph analysis.
 */
export interface TextureBindingInfo {
  /** Whether the system uses textures */
  usesTexture: boolean;
  /** Texture variable slot index (0-7), or -1 if not used */
  textureSlot: number;
}

/**
 * Analyze graph to extract texture binding information.
 * Looks for Get Variable (Texture) nodes connected to output nodes.
 */
export function extractTextureBinding(graph: VFXGraph): TextureBindingInfo {
  const edges = graph.getEdges();
  const outputContext = graph.getContextByType('output');

  if (!outputContext) {
    return { usesTexture: false, textureSlot: -1 };
  }

  const outputNodes = graph.getNodesInContext(outputContext.id);

  for (const outputNode of outputNodes) {
    // Only quad/sprite/lit_quad support textures
    if (outputNode.type !== 'vfx.output.quad' &&
        outputNode.type !== 'vfx.output.sprite' &&
        outputNode.type !== 'vfx.output.lit_quad') {
      continue;
    }

    // Find edge connected to texture input
    const textureEdge = edges.find(
      e => e.to.nodeId === outputNode.id && e.to.portId === 'texture'
    );

    if (textureEdge) {
      // Find the source node (should be Get Variable (Texture))
      const sourceNode = graph.getNode(textureEdge.from.nodeId);
      if (sourceNode && sourceNode.type === 'vfx.variable.get_texture') {
        const textureSlot = (sourceNode.inputValues.variableId as number) ?? 0;
        return { usesTexture: true, textureSlot };
      }
      // Connected but not to Get Variable (Texture) - still uses texture, slot 0
      return { usesTexture: true, textureSlot: 0 };
    }

    // Quad/sprite/lit_quad ALWAYS use texture - default texture if not connected
    return { usesTexture: true, textureSlot: -1 };
  }

  // No quad/sprite/lit_quad output node found (e.g., point, line) - no texture needed
  return { usesTexture: false, textureSlot: -1 };
}
